import React, { useMemo } from 'react'
import {
  ContextualMenu,
  Search,
  type ContextualMenuItem,
} from '@enamelai/design-system'
import { Icon as AddIcon } from '@enamelai/design-system/icons/Add_2'
import { Icon as CheckmarkIcon } from '@enamelai/design-system/icons/Checkmark'
import { Icon as DeleteIcon } from '@enamelai/design-system/icons/Delete2'
import { Icon as DraftsIcon } from '@enamelai/design-system/icons/Drafts'
import type {
  TemplateCategory,
  SelectedTemplateItem,
  TemplateItem,
} from '../types'
import FormIcon from './FormIcon'
import './TemplatesSidebar.css'

const DRAFTS_GROUP_TITLE = 'Drafts'

interface TemplatesSidebarProps {
  templateCategories: TemplateCategory[]
  templateSearch: string
  onTemplateSearchChange: (value: string) => void
  selectedCategory: string | null
  selectedTemplateItem: SelectedTemplateItem | null
  /** Adds a form to a category. Called without a category for the Drafts group. */
  onCreateCategoryForm: (categoryTitle?: string) => void
  onTemplateItemSelect: (
    item: SelectedTemplateItem,
    categoryTitle: string,
  ) => void
  onTemplateApprove?: (templateName: string, categoryTitle: string) => void
  onTemplateDelete?: (templateName: string, categoryTitle: string) => void
  onTemplateMoveToDraft?: (templateName: string, categoryTitle: string) => void
  hasUnsavedChanges?: boolean
}

interface SidebarEntry {
  item: TemplateItem
  /** The category the template is stored under (drafts keep theirs). */
  categoryTitle: string
}

interface SidebarGroup {
  title: string
  /** Undefined for the Drafts group, which is not a real category. */
  categoryTitle?: string
  entries: SidebarEntry[]
}

const compareByName = (a: SidebarEntry, b: SidebarEntry): number =>
  a.item.name.localeCompare(b.item.name, undefined, { sensitivity: 'base' })

const TemplatesSidebar: React.FC<TemplatesSidebarProps> = ({
  templateCategories,
  templateSearch,
  onTemplateSearchChange,
  selectedCategory,
  selectedTemplateItem,
  onCreateCategoryForm,
  onTemplateItemSelect,
  onTemplateApprove,
  onTemplateDelete,
  onTemplateMoveToDraft,
  hasUnsavedChanges = false,
}) => {
  const hasActions = Boolean(
    onTemplateApprove || onTemplateDelete || onTemplateMoveToDraft,
  )

  const groups = useMemo<SidebarGroup[]>(() => {
    const query = templateSearch.trim().toLowerCase()
    const matches = (entry: SidebarEntry): boolean =>
      query === '' || entry.item.name.toLowerCase().includes(query)

    const categoryGroups = templateCategories.map<SidebarGroup>((category) => ({
      title: category.title,
      categoryTitle: category.title,
      entries: category.items
        .filter((item) => item.status !== 'draft')
        .map((item) => ({ item, categoryTitle: category.title }))
        .filter(matches)
        .sort(compareByName),
    }))
    const draftEntries = templateCategories
      .flatMap((category) =>
        category.items
          .filter((item) => item.status === 'draft')
          .map((item) => ({ item, categoryTitle: category.title })),
      )
      .filter(matches)
      .sort(compareByName)

    const all = [
      ...categoryGroups,
      { title: DRAFTS_GROUP_TITLE, entries: draftEntries },
    ]
    return query === '' ? all : all.filter((group) => group.entries.length > 0)
  }, [templateCategories, templateSearch])

  const getMenuItems = (
    item: TemplateItem,
    categoryTitle: string,
  ): ContextualMenuItem[] => {
    const menuItems: ContextualMenuItem[] = []
    if (onTemplateApprove && item.status !== 'approved') {
      menuItems.push({
        id: 'approve',
        label: hasUnsavedChanges
          ? 'Approve (save or discard changes first)'
          : 'Approve',
        icon: <CheckmarkIcon aria-hidden />,
        disabled: hasUnsavedChanges,
        onSelect: () => onTemplateApprove(item.name, categoryTitle),
      })
    }
    if (onTemplateMoveToDraft && item.status !== 'draft') {
      menuItems.push({
        id: 'draft',
        label: 'Mark as draft',
        icon: <DraftsIcon aria-hidden />,
        onSelect: () => onTemplateMoveToDraft(item.name, categoryTitle),
      })
    }
    if (onTemplateDelete) {
      menuItems.push({
        id: 'delete',
        label: 'Delete',
        icon: <DeleteIcon aria-hidden />,
        onSelect: () => onTemplateDelete(item.name, categoryTitle),
      })
    }
    return menuItems
  }

  return (
    <aside className="create-form-sidebar-left" aria-label="Templates">
      <div className="templates-header">
        <h3 className="templates-title">Templates</h3>
      </div>
      <Search
        placeholder="Search"
        ariaLabel="Search template"
        value={templateSearch}
        onChange={onTemplateSearchChange}
        onClear={() => onTemplateSearchChange('')}
      />
      <div className="templates-list">
        {groups.map((group) => (
          <div key={group.title} className="template-category">
            <div className="template-category-header">
              <span className="template-category-title">{group.title}</span>
              <button
                type="button"
                className="template-category-add"
                aria-label={
                  group.categoryTitle
                    ? `Create a ${group.title} form`
                    : 'Create a new form'
                }
                onClick={() => onCreateCategoryForm(group.categoryTitle)}
              >
                <AddIcon aria-hidden />
              </button>
            </div>
            {group.entries.map(({ item, categoryTitle }) => {
              const isSelected =
                selectedTemplateItem?.name === item.name &&
                selectedCategory === categoryTitle
              const menuItems = hasActions
                ? getMenuItems(item, categoryTitle)
                : []
              return (
                <div
                  key={`${categoryTitle}|${item.form_template_id ?? item.name}`}
                  className={`template-item${isSelected ? ' selected' : ''}`}
                >
                  <button
                    type="button"
                    className="template-item-left"
                    aria-pressed={isSelected}
                    onClick={() =>
                      onTemplateItemSelect(
                        {
                          name: item.name,
                          status: item.status,
                          form_template_id: item.form_template_id,
                        },
                        categoryTitle,
                      )
                    }
                  >
                    <FormIcon />
                    <span className="template-item-name-container">
                      <span className="template-item-name">{item.name}</span>
                      {item.status === 'pending' && (
                        <span className="template-item-status">- pending</span>
                      )}
                    </span>
                  </button>
                  {menuItems.length > 0 && (
                    <ContextualMenu
                      className="template-item-actions"
                      ariaLabel={`Actions for ${item.name}`}
                      items={menuItems}
                    />
                  )}
                </div>
              )
            })}
          </div>
        ))}
      </div>
    </aside>
  )
}

export default TemplatesSidebar
